<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { Bell, CheckCheck } from '@lucide/vue';
import type { NotificaVista } from '@evolfin/shared';
import {
  contaNonLette,
  listaNotifiche,
  segnaLetta,
  segnaTutteLette,
} from '@/features/notifiche/api';
import { formattaDataOra } from '@/utils/formato';

/**
 * Campana delle notifiche nella shell: badge non-lette (polling) e pannello con
 * le ultime notifiche. Click su una notifica → segna letta e naviga al link.
 * L'autorizzazione è per-utente lato server; qui è solo presentazione.
 */
const router = useRouter();
const client = useQueryClient();
const aperto = ref(false);
const radice = ref<HTMLElement | null>(null);

// Badge: leggero e sempre attivo (poll ogni 60s). La lista si carica solo quando
// il pannello è aperto, per non tenere query inutili.
const conteggio = useQuery({
  queryKey: ['notifiche', 'conteggio'],
  queryFn: contaNonLette,
  refetchInterval: 60_000,
});
const lista = useQuery({
  queryKey: ['notifiche', 'lista'],
  queryFn: () => listaNotifiche({ pageSize: 8 }),
  enabled: aperto,
});

const nonLette = computed(() => conteggio.data.value?.nonLette ?? 0);
const badge = computed(() => (nonLette.value > 9 ? '9+' : String(nonLette.value)));
const notifiche = computed<NotificaVista[]>(() => lista.data.value?.items ?? []);

function aggiorna(): void {
  void client.invalidateQueries({ queryKey: ['notifiche'] });
}

const mutaLetta = useMutation({ mutationFn: segnaLetta, onSuccess: aggiorna });
const mutaTutte = useMutation({ mutationFn: segnaTutteLette, onSuccess: aggiorna });

async function apri(n: NotificaVista): Promise<void> {
  if (!n.lettaIl) await mutaLetta.mutateAsync(n.id).catch(() => undefined);
  aperto.value = false;
  const link = typeof n.dati.link === 'string' ? n.dati.link : null;
  if (link) await router.push(link).catch(() => undefined);
}

// Chiusura al click fuori dal componente.
function chiudiSeFuori(e: MouseEvent): void {
  if (radice.value && !radice.value.contains(e.target as Node)) aperto.value = false;
}
onMounted(() => document.addEventListener('click', chiudiSeFuori));
onBeforeUnmount(() => document.removeEventListener('click', chiudiSeFuori));
</script>

<template>
  <div ref="radice" class="relative">
    <button
      type="button"
      class="relative grid size-9 place-items-center rounded-sm text-ink-secondary transition-colors hover:bg-white hover:text-ink"
      :aria-label="`Notifiche${nonLette ? ` (${nonLette} non lette)` : ''}`"
      @click="aperto = !aperto"
    >
      <Bell :size="18" :stroke-width="1.8" />
      <span
        v-if="nonLette > 0"
        class="absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-pill bg-[var(--color-primary)] px-1 text-[10px] font-semibold text-[var(--color-on-primary)]"
      >
        {{ badge }}
      </span>
    </button>

    <!-- Pannello: si apre verso il basso, sotto la campana in cima alla sidebar. -->
    <div
      v-if="aperto"
      class="absolute top-full left-0 z-20 mt-2 w-80 overflow-hidden rounded-lg border border-line-soft bg-surface shadow-[var(--shadow-card-strong)]"
    >
      <div class="flex items-center justify-between border-b border-line-soft px-4 py-2.5">
        <span class="text-[13px] font-semibold">Notifiche</span>
        <button
          v-if="nonLette > 0"
          type="button"
          class="flex items-center gap-1 text-[12px] text-[var(--color-primary)] hover:underline"
          @click="mutaTutte.mutate()"
        >
          <CheckCheck :size="14" :stroke-width="1.9" />
          Segna tutte lette
        </button>
      </div>

      <div class="max-h-96 overflow-y-auto">
        <p v-if="lista.isPending.value" class="px-4 py-6 text-center text-[13px] text-ink-muted">
          Caricamento…
        </p>
        <p
          v-else-if="notifiche.length === 0"
          class="px-4 py-8 text-center text-[13px] text-ink-secondary"
        >
          Nessuna notifica.
        </p>
        <button
          v-for="n in notifiche"
          v-else
          :key="n.id"
          type="button"
          class="flex w-full items-start gap-2.5 border-b border-line-soft px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-soft"
          @click="apri(n)"
        >
          <span
            class="mt-1.5 size-2 shrink-0 rounded-full"
            :class="n.lettaIl ? 'bg-transparent' : 'bg-[var(--color-accent)]'"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1">
            <span
              class="block text-[13px] font-medium text-ink"
              :class="{ 'font-semibold': !n.lettaIl }"
            >
              {{ n.titolo }}
            </span>
            <span class="mt-0.5 block truncate text-[12.5px] text-ink-secondary">{{
              n.corpo
            }}</span>
            <span class="mt-1 block text-[11px] text-ink-muted">{{
              formattaDataOra(n.creatoIl)
            }}</span>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
